/**
 * The Terminal tab: a command line in the open folder, run by the system shell (PowerShell or cmd
 * on Windows, the login shell on Linux and macOS), its output shown here. Desktop only — in a
 * browser it says so and points to the desktop app.
 *
 * Enter runs, ↑/↓ walk the history (kept for the browser session), Ctrl+C or Stop ends the command
 * and what it started. Each command is its own process started in the folder, so `cd` lasts for
 * that command only.
 *
 * What is on screen lives in this module rather than in the component, so switching dock tabs and
 * back keeps the output and a command still running.
 */

import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { Eraser, Play, Square, SquareTerminal } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { isDesktop } from '../lib/desktop'
import { canRun, capLines, navigateHistory, newRunId, pushHistory, runTerminal, stripAnsi, terminalShell, type TerminalShell } from '../lib/terminal'

type LineKind = 'cmd' | 'stdout' | 'stderr' | 'info' | 'error'

interface Line {
  id: number
  kind: LineKind
  text: string
}

// ------------------------------------------------------------------------------ the session

const HISTORY_KEY = 'devpulse.terminalHistory'

function readHistory(): string[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(HISTORY_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

interface Session {
  lines: Line[]
  history: string[]
  running: { runId: string; stop: () => Promise<void> } | null
}

let session: Session = { lines: [], history: readHistory(), running: null }
let nextLine = 1
const listeners = new Set<() => void>()

function update(change: (current: Session) => Session) {
  session = change(session)
  for (const listener of listeners) listener()
}

function append(kind: LineKind, text: string) {
  update((s) => ({ ...s, lines: capLines([...s.lines, { id: nextLine++, kind, text }]) }))
}

function useSession(): Session {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => session,
  )
}

// ------------------------------------------------------------------------------ the panel

/** A command to run as soon as the panel opens: the prompt box's `!command` (lib/composer.ts). */
export interface TerminalRun {
  command: string
  /** A new one runs again even with the same text. */
  nonce: number
}

export function TerminalPanel({ folder, run = null }: { folder: string | null; run?: TerminalRun | null }) {
  return isDesktop() ? <DesktopTerminal folder={folder} given={run} /> : <WebTerminal />
}

function WebTerminal() {
  const { t } = useI18n()
  return (
    <div className="terminal terminal--web">
      <SquareTerminal size={18} aria-hidden />
      <div className="stack" style={{ gap: 4 }}>
        <strong className="small">{t('terminal.desktopOnlyTitle')}</strong>
        <span className="small muted">{t('terminal.desktopOnly')}</span>
        <Link to="/agents/connect?tab=desktop" className="small">{t('terminal.getDesktop')}</Link>
      </div>
    </div>
  )
}

function DesktopTerminal({ folder, given }: { folder: string | null; given: TerminalRun | null }) {
  const { t } = useI18n()
  const { lines, history, running } = useSession()
  const [shell, setShell] = useState<TerminalShell | null>(null)
  const [input, setInput] = useState('')
  const [cursor, setCursor] = useState(history.length)
  const [draft, setDraft] = useState('')
  const output = useRef<HTMLDivElement>(null)
  const stick = useRef(true)
  const field = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let alive = true
    terminalShell().then((found) => alive && setShell(found)).catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  // Follows the output while the person is at the bottom; leaves them be when they scrolled up.
  useLayoutEffect(() => {
    const box = output.current
    if (box && stick.current) box.scrollTop = box.scrollHeight
  }, [lines])

  const folderName = folder ? folder.split(/[\\/]/).filter(Boolean).pop() ?? folder : null
  const promptMark = shell?.id === 'powershell' ? 'PS>' : shell?.id === 'cmd' ? '>' : '$'

  const run = async (typed?: string) => {
    const command = typed ?? input
    if (!folder || running || !canRun(command)) return
    const nextHistory = pushHistory(session.history, command)
    try {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(nextHistory))
    } catch {
      // History lasts as long as the page, then.
    }
    update((s) => ({ ...s, history: nextHistory }))
    setInput('')
    setDraft('')
    setCursor(nextHistory.length)
    stick.current = true
    append('cmd', `${folderName ?? ''} ${promptMark} ${command}`)

    const runId = newRunId()
    let exited = false
    // Running from now, so a second Enter waits; Stop works once the desktop has started it.
    update((s) => ({ ...s, running: { runId, stop: async () => {} } }))
    try {
      const stopRun = await runTerminal(runId, folder, command, {
        onLine: (line, stream) => append(stream, stripAnsi(line)),
        onExit: (code) => {
          exited = true
          update((s) => (s.running?.runId === runId ? { ...s, running: null } : s))
          if (code !== 0) append('info', code === null ? t('terminal.stopped') : t('terminal.exitCode', { code }))
          requestAnimationFrame(() => field.current?.focus())
        },
      })
      // The end may already have come with a quick command.
      if (!exited) update((s) => (s.running?.runId === runId ? { ...s, running: { runId, stop: stopRun } } : s))
    } catch (error) {
      update((s) => (s.running?.runId === runId ? { ...s, running: null } : s))
      const message = String(error)
      append('error', message.includes('folder-not-allowed') ? t('terminal.error.folder') : message.includes('invalid-command') ? t('terminal.error.command') : t('terminal.error.failed', { message }))
    }
  }

  // `!command` from the prompt box: run once per nonce (StrictMode mounts twice; the nonce does not repeat).
  const ran = useRef<number | null>(null)
  useEffect(() => {
    if (!given || !folder || ran.current === given.nonce) return
    ran.current = given.nonce
    void run(given.command)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run reads the latest state each time
  }, [given, folder])

  const stop = () => {
    const current = session.running
    if (!current) return
    void current.stop().catch(() => {})
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      void run()
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      const from = cursor === history.length ? input : draft
      const moved = navigateHistory(history, cursor, event.key === 'ArrowUp' ? 'up' : 'down', from)
      if (cursor === history.length) setDraft(input)
      setCursor(moved.index)
      setInput(moved.value)
    } else if (event.ctrlKey && event.key.toLowerCase() === 'c') {
      const target = event.currentTarget
      const selected = target.selectionStart !== target.selectionEnd
      if (selected) return // a normal copy
      event.preventDefault()
      if (session.running) stop()
      else {
        setInput('')
        setCursor(history.length)
      }
    } else if (event.ctrlKey && event.key.toLowerCase() === 'l') {
      event.preventDefault()
      update((s) => ({ ...s, lines: [] }))
    }
  }

  return (
    <div className="terminal">
      <div className="terminal__bar">
        <SquareTerminal size={14} aria-hidden />
        <strong className="tiny">{shell?.name ?? t('terminal.title')}</strong>
        {folder ? (
          <span className="tiny muted mono truncate" title={folder}>{folder}</span>
        ) : (
          <span className="tiny muted">{t('terminal.noFolder')}</span>
        )}
        <span className="spacer" />
        {running && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={stop} title={`${t('terminal.stop')} (Ctrl+C)`}>
            <Square size={12} /> {t('terminal.stop')}
          </button>
        )}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => update((s) => ({ ...s, lines: [] }))} disabled={lines.length === 0} title={`${t('terminal.clear')} (Ctrl+L)`}>
          <Eraser size={12} /> {t('terminal.clear')}
        </button>
      </div>

      <div
        ref={output}
        className="terminal__output mono"
        role="log"
        aria-live="polite"
        aria-label={t('terminal.output')}
        onScroll={(event) => {
          const box = event.currentTarget
          stick.current = box.scrollHeight - box.scrollTop - box.clientHeight < 24
        }}
        onClick={() => {
          if (!window.getSelection()?.toString()) field.current?.focus()
        }}
      >
        {lines.length === 0 && <div className="terminal__hint">{t('terminal.hint')}</div>}
        {lines.map((line) => (
          <div key={line.id} className={`terminal__line terminal__line--${line.kind}`}>{line.text || ' '}</div>
        ))}
      </div>

      <div className="terminal__input">
        <span className="terminal__prompt mono" aria-hidden>{promptMark}</span>
        <input
          ref={field}
          className="terminal__field mono"
          value={input}
          onChange={(event) => {
            setInput(event.target.value)
            setCursor(history.length)
          }}
          onKeyDown={onKeyDown}
          placeholder={folder ? (running ? t('terminal.running') : t('terminal.placeholder')) : t('terminal.noFolder')}
          aria-label={t('terminal.input')}
          disabled={!folder}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
        />
        <button type="button" className="btn btn--primary btn--sm" onClick={() => void run()} disabled={!folder || Boolean(running) || !canRun(input)} title={`${t('terminal.run')} (Enter)`}>
          <Play size={12} /> {t('terminal.run')}
        </button>
      </div>
    </div>
  )
}
